import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { validaCpfCnpj } from 'utils/validation';
import {
  labelsCaracteristicas,
  labelsCriteriosRemuneracao,
  labelsEmissor,
} from 'pages/cdca/registro/labels';
import { labelsEventosFinanceiros } from 'common/registro/labels';
import { documentShortDescriptions } from 'common/genericDescriptions';
import { BooleanValue } from 'common/genericOptions';
import { Moeda, IndexadorRemuneracao, PeriodicidadeCorrecao, GrupoAcesso, FuncaoParticipante } from 'common/genericTypes';

type labelsType =
  | typeof labelsCaracteristicas
  | typeof labelsCriteriosRemuneracao
  | typeof labelsEventosFinanceiros
  | typeof labelsEmissor;

export enum EtapaCadastro {
  Caracteristicas,
  CriteriosRemuneracao,
  EventosFinanceiros,
  Documentos,
  Emissor,
}

export function useValidarCampos(label: labelsType): boolean;
export function useValidarCampos(etapa?: EtapaCadastro): Array<string>;

export function useValidarCampos(arg?: EtapaCadastro | labelsType): Array<string> | boolean {
  const caracteristicas = useSelector((state: RootState) => state.cdcaCadastro.caracteristicas);
  const criteriosRemuneracao = useSelector((state: RootState) => state.cdcaCadastro.criteriosRemuneracao);
  const eventosFinanceiros = useSelector((state: RootState) => state.cdcaCadastro.eventos);
  const uploadDocumentos = useSelector((state: RootState) => state.cdcaCadastro.uploadDocumentos);
  const emissor = useSelector((state: RootState) => state.cdcaCadastro.emissor);
  const grupoAcesso = useSelector((state: RootState) => state.auth.usuario.grupoAcesso);
  const isUsuarioCadastroOuOperacoes =
    grupoAcesso === GrupoAcesso.CadastroOperacoes || grupoAcesso === GrupoAcesso.Operacoes;
  const isParticipanteCustodiante = useSelector(
    (state: RootState) => state.auth.usuario.participante.funcao === FuncaoParticipante.Custodiante
  );
  let camposInvalidosCE: Array<string> = [];

  if ((!emissor.id && isUsuarioCadastroOuOperacoes) || (!caracteristicas.cnpjBancoLiquidanteEmissor && isParticipanteCustodiante)) camposInvalidosCE.push(labelsEmissor.emissor);

  //Características da Emissão
  if (!caracteristicas.numeroEmissao) camposInvalidosCE.push(labelsCaracteristicas.numeroEmissao);

  if (!caracteristicas.numeroSerie) camposInvalidosCE.push(labelsCaracteristicas.numeroSerie);

  if (!caracteristicas.regimeFiduciario.value) camposInvalidosCE.push(labelsCaracteristicas.regimeFiduciario);

  if (!caracteristicas.distribuicaoPublica.value) camposInvalidosCE.push(labelsCaracteristicas.distribuicaoPublica);

  if (!caracteristicas.negociacaoSecundariaPor.value)
    camposInvalidosCE.push(labelsCaracteristicas.negociacaoSecundariaPor);

  if (!caracteristicas.termoSecuritizacaoFormalizado.value)
    camposInvalidosCE.push(labelsCaracteristicas.termoSecuritizacaoFormalizado);

  if (!caracteristicas.depositariaEmissao.value) camposInvalidosCE.push(labelsCaracteristicas.depositariaEmissao);

  if (!caracteristicas.codigoCvmCiaSecuritizadora)
    camposInvalidosCE.push(labelsCaracteristicas.codigoCvmCiaSecuritizadora);

  if (!caracteristicas.ufLocalEmissao.value) camposInvalidosCE.push(labelsCaracteristicas.ufLocalEmissao);

  if (!caracteristicas.localEmissao) camposInvalidosCE.push(labelsCaracteristicas.localEmissao);

  if (caracteristicas.classificadoraRisco1 && !caracteristicas.rating1)
    camposInvalidosCE.push(labelsCaracteristicas.rating1);

  if (caracteristicas.classificadoraRisco2 && !caracteristicas.rating2)
    camposInvalidosCE.push(labelsCaracteristicas.rating2);

  if (!caracteristicas.ufLocalPagamento.value) camposInvalidosCE.push(labelsCaracteristicas.ufLocalPagamento);

  if (!caracteristicas.localPagamento) camposInvalidosCE.push(labelsCaracteristicas.localPagamento);

  if (!caracteristicas.nomeResponsavelPelasInformacoesDRI)
    camposInvalidosCE.push(labelsCaracteristicas.nomeResponsavelPelasInformacoesDRI);

  if (!caracteristicas.emailResponsavelPelasInformacoesDRI)
    camposInvalidosCE.push(labelsCaracteristicas.emailResponsavelPelasInformacoesDRI);

  if (!caracteristicas.tipoLiquidacao) camposInvalidosCE.push(labelsCaracteristicas.tipoLiquidacao);

  if (
    !caracteristicas.cnpjBancoLiquidanteEmissor ||
    !validaCpfCnpj(caracteristicas.cnpjBancoLiquidanteEmissor) ||
    !caracteristicas.codigoBancoContaCorrenteVinculadaEmissao
  )
    camposInvalidosCE.push(labelsCaracteristicas.bancoLiquidanteEmissor);

  if (!caracteristicas.numeroAgenciaContaCorrenteVinculadaEmissao)
    camposInvalidosCE.push(labelsCaracteristicas.numeroAgenciaContaCorrenteVinculadaEmissao);

  if (!caracteristicas.numeroContaCorrenteVinculadaEmissao)
    camposInvalidosCE.push(labelsCaracteristicas.numeroContaCorrenteVinculadaEmissao);

  if (
    !caracteristicas.razaoSocialEscriturador ||
    !caracteristicas.cnpjEscriturador ||
    !validaCpfCnpj(caracteristicas.cnpjEscriturador)
  )
    camposInvalidosCE.push(labelsCaracteristicas.escriturador);

  //Informações do Lastro
  if (!caracteristicas.averbadoEmCartorio.value) camposInvalidosCE.push(labelsCaracteristicas.averbadoEmCartorio);

  if (!caracteristicas.registroNaInstituicaoCustodiante.value)
    camposInvalidosCE.push(labelsCaracteristicas.registroNaInstituicaoCustodiante);

  if (!caracteristicas.garantiaFlutuante.value) camposInvalidosCE.push(labelsCaracteristicas.garantiaFlutuante);

  if (!caracteristicas.tipoGarantia) camposInvalidosCE.push(labelsCaracteristicas.tipoGarantia);

  if (!caracteristicas.ativoGarantia) camposInvalidosCE.push(labelsCaracteristicas.ativoGarantia);

  if (!caracteristicas.nomeGarantidor) camposInvalidosCE.push(labelsCaracteristicas.nomeGarantidor);

  if (!caracteristicas.cpfCnpjGarantidor || !validaCpfCnpj(caracteristicas.cpfCnpjGarantidor))
    camposInvalidosCE.push(labelsCaracteristicas.cpfCnpjGarantidor);

  if (!caracteristicas.naturezaGarantidor.value) camposInvalidosCE.push(labelsCaracteristicas.naturezaGarantidor);

  if (!caracteristicas.valorGarantia) camposInvalidosCE.push(labelsCaracteristicas.valorGarantia);

  //Regime Fiduciário
  if (caracteristicas.regimeFiduciario.value === BooleanValue.Yes) {
    if (!caracteristicas.coobrigacao.value) camposInvalidosCE.push(labelsCaracteristicas.coobrigacao);

    if (!caracteristicas.razaoSocialAgenteFiduciario)
      camposInvalidosCE.push(labelsCaracteristicas.razaoSocialAgenteFiduciario);

    if (!caracteristicas.cpfCnpjAgenteFiduciario || !validaCpfCnpj(caracteristicas.cpfCnpjAgenteFiduciario))
      camposInvalidosCE.push(labelsCaracteristicas.cpfCnpjAgenteFiduciario);
  }

  //Distribuição Pública
  if (caracteristicas.distribuicaoPublica.value === BooleanValue.Yes) {
    if (!caracteristicas.tipoDistribuicaoPublica.value)
      camposInvalidosCE.push(labelsCaracteristicas.tipoDistribuicaoPublica);

    if (caracteristicas.coordenadoresIds.length === 0) camposInvalidosCE.push(labelsCaracteristicas.coordenadores);

    if (!caracteristicas.coordenadorLiderId) camposInvalidosCE.push(labelsCaracteristicas.coordenadorLider);
  }

  if (arg === EtapaCadastro.Caracteristicas) return camposInvalidosCE;

  let camposInvalidosCR: Array<string> = [];
  //Informações Financeiras
  if (!criteriosRemuneracao.valorNominalUnitarioEmissao)
    camposInvalidosCR.push(labelsCriteriosRemuneracao.valorNominalUnitarioEmissao);

  if (!criteriosRemuneracao.quantidadeEmitida) camposInvalidosCR.push(labelsCriteriosRemuneracao.quantidadeEmitida);

  if (!criteriosRemuneracao.moedaEmissao) camposInvalidosCR.push(labelsCriteriosRemuneracao.moedaEmissao);

  if (!criteriosRemuneracao.dataEmissao) camposInvalidosCR.push(labelsCriteriosRemuneracao.dataEmissao);

  if (!criteriosRemuneracao.dataVencimento) camposInvalidosCR.push(labelsCriteriosRemuneracao.dataVencimento);

  if (criteriosRemuneracao.custodiantes.filter((custodiante) => custodiante.investidoresPrivados.length === 0).length > 0)
  {
    camposInvalidosCR.push(labelsCriteriosRemuneracao.investidores);
  }

  if (criteriosRemuneracao.moedaEmissao !== Moeda.BRL && !criteriosRemuneracao.cotacaoInicial)
    camposInvalidosCR.push(labelsCriteriosRemuneracao.cotacaoInicial);

  //Remuneração
  if (!criteriosRemuneracao.remuneracao.indexador) camposInvalidosCR.push(labelsCriteriosRemuneracao.indexador);

  if (
    [IndexadorRemuneracao.Selic, IndexadorRemuneracao.CDI].some(
      (i) => i === criteriosRemuneracao.remuneracao.indexador
    ) &&
    !criteriosRemuneracao.remuneracao.indexadorPorcentagem
  )
    camposInvalidosCR.push(labelsCriteriosRemuneracao.indexadorPorcentagem);

  if (IndexadorRemuneracao.Prefixado === criteriosRemuneracao.remuneracao.indexador) {
    if (!criteriosRemuneracao.remuneracao.taxaJurosFixoSpreadPorcentagem)
      camposInvalidosCR.push(labelsCriteriosRemuneracao.taxaJurosFixoSpreadPorcentagem);

    if (!criteriosRemuneracao.remuneracao.convencao) camposInvalidosCR.push(labelsCriteriosRemuneracao.convencao);
  } else {
    if (!criteriosRemuneracao.remuneracao.defasagemFixing)
      camposInvalidosCR.push(labelsCriteriosRemuneracao.defasagemFixing);
  }

  if (
    [IndexadorRemuneracao.IGPM, IndexadorRemuneracao.IPCA].some((i) => i === criteriosRemuneracao.remuneracao.indexador)
  ) {
    if (!criteriosRemuneracao.remuneracao.periodicidadeCorrecao)
      camposInvalidosCR.push(labelsCriteriosRemuneracao.periodicidadeCorrecao);

    if (!criteriosRemuneracao.remuneracao.proRata) camposInvalidosCR.push(labelsCriteriosRemuneracao.proRata);

    if (
      criteriosRemuneracao.remuneracao.periodicidadeCorrecao === PeriodicidadeCorrecao.Anual &&
      criteriosRemuneracao.remuneracao.corrigePeriodoFinal === undefined
    )
      camposInvalidosCR.push(labelsCriteriosRemuneracao.corrigePeriodoFinal);

    if (!criteriosRemuneracao.remuneracao.diaAniversario)
      camposInvalidosCR.push(labelsCriteriosRemuneracao.diaAniversario);

    if (!criteriosRemuneracao.remuneracao.mesAniversario && criteriosRemuneracao.remuneracao.periodicidadeCorrecao === PeriodicidadeCorrecao.Anual)
      camposInvalidosCR.push(labelsCriteriosRemuneracao.mesAniversario);

    if (!criteriosRemuneracao.remuneracao.tipoProjecao) camposInvalidosCR.push(labelsCriteriosRemuneracao.tipoProjecao);
  }

  if (arg === EtapaCadastro.CriteriosRemuneracao) return camposInvalidosCR;

  let camposInvalidosEF: Array<string> = [];
  if (eventosFinanceiros.amortizacoes.length === 0) camposInvalidosEF.push('Pagamento de Amortização');

  if (eventosFinanceiros.amortizacoes.reduce((acc, obj) => { return acc + (obj.percentualAmortizacao ?? 0)}, 0) > 100) camposInvalidosEF.push('Percentual de Amortização Total acima de 100%');

  if (eventosFinanceiros.datasPagamentoJuros?.length === 0) camposInvalidosEF.push('Pagamento de Juros');

  if (arg === EtapaCadastro.EventosFinanceiros) return camposInvalidosEF;

  let camposInvalidosDocs: Array<string> = [];
  if (uploadDocumentos.documents.length === 0) {
    camposInvalidosDocs.push('Documentos');
  } else {
    uploadDocumentos.documents.forEach((doc) => {
      if (!doc.optional && !doc.file && !doc.remoteFileInfo) {
        const documentoLabel = documentShortDescriptions[doc.type];
        camposInvalidosDocs.push(`${documentoLabel} - Documentos`);
      }
    });
  }

  if (!criteriosRemuneracao.custoEmissao.custoTotal) camposInvalidosCR.push(labelsCriteriosRemuneracao.custoTotal);

  if (arg === EtapaCadastro.Documentos) return camposInvalidosDocs;

  //Emissor
  let camposInvalidosEmissor: Array<string> = [];
  if (!emissor.id) camposInvalidosEmissor.push(labelsEmissor.emissor);

  if (!emissor.codigoExterno) camposInvalidosEmissor.push(labelsEmissor.codigoExterno);

  if (arg === EtapaCadastro.Emissor) return camposInvalidosEmissor;

  const camposInvalidos = camposInvalidosCE
    .concat(camposInvalidosCR)
    .concat(camposInvalidosEF)
    .concat(camposInvalidosDocs);
  return typeof arg === 'string' ? camposInvalidos.includes(arg) : camposInvalidos;
}
